import React, { useMemo, useState } from 'react';
import { AskBackSlot, ASK_BACK_SLOT_STATE, Candidate } from '../../../../../../../../store/globalAskAica/types';
import { ambiguousSlots, PickedSlot, requiresChoice } from '../../../../../../../../store/globalAskAica/askBackSlots';
import { GLOBAL_ASK_AICA_COPY } from '../../../../../../../../store/globalAskAica/constants';
import CrossIcon from '../../../../../../../../assets/Svg/CrossIcon';
import styles from './CompanyPicker.module.scss';

// PickedSlot is declared in the store beside buildGateReply (the hook consumes it
// too) — re-exported here so component-local imports stay short.
export type { PickedSlot };

interface CompanyPickerProps {
    /** One slot per name the user wrote — ALREADY normalised (see
     * store/globalAskAica/askBackSlots.ts::normaliseAskBackSlots), so this
     * component never has to know whether the payload came from a
     * RECUR-27888 Athena or the legacy `candidates`-only one live today. */
    slots: AskBackSlot[];
    /** Fires once, on Confirm, with EVERY ambiguous slot's choice — the submission
     * is atomic because a partial one is rejected server-side and re-prompts. */
    onConfirm: (picked: PickedSlot[]) => void;
    /** The card's "✕" — now abandons the whole form, not just a single question
     * (see Figma 32804-43057 for the affordance itself). */
    onDismiss?: () => void;
    disabled?: boolean;
}

/**
 * Renders an ask_back(kind:"pick") gate as a FORM over `slots` — frozen contract
 * v4 §4. Each name the user wrote gets one group:
 *
 *  - `ambiguous`     → interactive radio rows, REQUIRED. Two-or-more candidates is
 *                      the disambiguation case; exactly one is the resolver's
 *                      "Did you mean X?" confirm — the same card either way, which
 *                      is why `confirm` needs no third render (v4 §3).
 *  - `resolved`      → a LOCKED row naming what the write matched. It exists so a
 *                      wrong auto-match is visible rather than silent — that is the
 *                      whole point of showing a row nobody can change.
 *  - `not_in_book` /
 *    `search_failed` → a MARKED row pointing at the composer. Deliberately not an
 *                      inline input: RECUR-27369 removed the free-text row and
 *                      CompanyPicker.test.tsx asserts its absence.
 *
 * 🔴 A single ambiguous slot must render as the shipped production build does —
 * same title, one row per candidate, avatar initial, name left / CIN right, never
 * a city, "✕" present, no free-text row. The per-slot group label is therefore
 * suppressed when there is only one slot, and the ONE intended behaviour change is
 * submission: a row tap now SELECTS, and Confirm submits.
 *
 * The raw ask_back.prompt (numbered-list text) is still never rendered here — the
 * card's title and rows already convey it, and PortfolioAskAicaSurfaceProvider
 * filters that turn's prompt out of the transcript.
 */
const CompanyPicker: React.FC<CompanyPickerProps> = ({ slots, onConfirm, onDismiss, disabled = false }) => {
    const [selections, setSelections] = useState<Record<string, Candidate>>({});

    const required = useMemo(() => ambiguousSlots(slots), [slots]);

    // A selection is honoured only when it points at a candidate of the slot
    // CURRENTLY on screen. `selections` outlives a gate: applyGateOutcome
    // replaces one pick gate with the next IN PLACE (no remount, no key), and
    // the legacy candidates-only payload gives every gate the same
    // LEGACY_SLOT_ID — so a raw read of `selections` rendered the new gate
    // with no checked row while Confirm stayed enabled and submitted the
    // PREVIOUS question's company (reproduced on this branch: pick ACC INDIA,
    // second gate for SAMARA, Confirm sends acc-1). Deriving validity from
    // the on-screen slot makes "Confirm is enabled iff every required group
    // shows a checked row" true by construction.
    const validSelections = useMemo(() => {
        // Null-prototype: slot_id is wire data, and on a plain literal a slot_id
        // of "toString"/"__proto__" would read an INHERITED function as a truthy
        // "selection" — enabling Confirm with no real pick (same class as the
        // answerScopeExcludedCopy own-keys guard).
        const valid: Record<string, Candidate> = Object.create(null);
        required.forEach((slot) => {
            const chosen = selections[slot.slot_id];
            if (chosen && slot.candidates?.some((candidate) => candidate.org_id === chosen.org_id)) {
                valid[slot.slot_id] = chosen;
            }
        });
        return valid;
    }, [required, selections]);

    // Confirm gates on EVERY ambiguous slot being answered — atomicity is a server
    // rule (a partial submission is rejected and the form re-prompted), so the
    // client must not allow what the server would only bounce.
    const allAnswered = required.length > 0 && required.every((slot) => !!validSelections[slot.slot_id]);

    // An empty slot list is a contract violation with nothing to show — absent.
    // But a payload whose slots are ALL static must still render: the demotion
    // path (guardUnsatisfiableSlot turning a candidate-less `ambiguous` into
    // `search_failed`) deliberately produces exactly this shape, and the picker
    // turn's streamed content is suppressed upstream — returning null here left
    // the whole turn BLANK, with PICKER_SEARCH_FAILED_HINT unreachable in the
    // one case it was written for. The static rows carry the explanation and
    // the "✕" is the exit, so this is not the dead end the old comment feared.
    if (slots.length === 0) return null;

    const showSlotLabels = slots.length > 1;

    const handleConfirm = () => {
        if (!allAnswered || disabled) return;
        onConfirm(required.map((slot) => ({ slot_id: slot.slot_id, candidate: validSelections[slot.slot_id] })));
    };

    return (
        <div className={styles.CompanyPicker} data-testid="company-picker">
            <div className={styles.Header}>
                <span className={styles.Title}>{GLOBAL_ASK_AICA_COPY.PICKER_TITLE}</span>
                {onDismiss && (
                    <button type="button" className={styles.DismissButton} onClick={onDismiss} aria-label="Dismiss">
                        <CrossIcon colour="#4d4d4d" width="12" height="12" />
                    </button>
                )}
            </div>

            <div className={styles.SlotList}>
                {slots.map((slot) => (
                    <div key={slot.slot_id} className={styles.Slot} data-testid="company-picker-slot" data-slot-state={slot.state}>
                        {showSlotLabels && (
                            <span className={styles.SlotLabel} data-testid="company-picker-slot-label">
                                {slot.written}
                            </span>
                        )}
                        {requiresChoice(slot) ? (
                            <div
                                className={styles.CandidateList}
                                role="radiogroup"
                                aria-label={slot.written || GLOBAL_ASK_AICA_COPY.PICKER_TITLE}
                            >
                                {slot.candidates?.map((candidate) => {
                                    const isSelected = validSelections[slot.slot_id]?.org_id === candidate.org_id;
                                    return (
                                        <button
                                            key={candidate.org_id}
                                            type="button"
                                            role="radio"
                                            aria-checked={isSelected}
                                            className={`${styles.CandidateRow} ${isSelected ? styles.Selected : ''}`}
                                            // Selects only. Submission moved to Confirm so every slot
                                            // can be answered before ONE atomic payload is sent.
                                            onClick={() => setSelections((prev) => ({ ...prev, [slot.slot_id]: candidate }))}
                                            disabled={disabled}
                                            data-testid="company-picker-row"
                                        >
                                            <span className={styles.RadioDot} aria-hidden="true" />
                                            {/* Design (Figma 32804:43164) shows a real company logo here. The
                                                ask_back candidate payload carries only org_id/name/cin/city — no
                                                website — and getCompanyLogo() needs a hostname, so this keeps the
                                                design's 24px square slot with the initial as a stand-in. Adding a
                                                `website` (or `logo`) field to the candidate payload is all that is
                                                needed to drop a real <img> in here. */}
                                            <span className={styles.LogoSlot} aria-hidden="true" data-testid="company-picker-avatar">
                                                {candidate.name.charAt(0).toUpperCase()}
                                            </span>
                                            <span className={styles.CandidateName}>{candidate.name}</span>
                                            {candidate.cin && <span className={styles.CandidateCin}>{candidate.cin}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <StaticSlotRow slot={slot} />
                        )}
                    </div>
                ))}
            </div>

            {required.length > 0 && (
                <button
                    type="button"
                    className={styles.ConfirmButton}
                    onClick={handleConfirm}
                    disabled={!allAnswered || disabled}
                    data-testid="company-picker-confirm"
                >
                    {GLOBAL_ASK_AICA_COPY.PICKER_CONFIRM}
                </button>
            )}
        </div>
    );
};

/**
 * A slot the user cannot act on: either already `resolved` (locked, so a wrong
 * auto-match is visible) or un-resolvable (marked, pointing at the composer).
 * Non-interactive by construction — a div, never a button — so it can't be
 * mistaken for a choice, and so it never lands in `company-picker-row`'s
 * selector and skews a candidate-row count.
 */
const StaticSlotRow: React.FC<{ slot: AskBackSlot }> = ({ slot }) => {
    const isResolved = slot.state === ASK_BACK_SLOT_STATE.RESOLVED;
    // `search_failed` keeps its own sentence: "we couldn't look this up" is a
    // different claim from "not in your portfolio", and telling a user the latter
    // when the lookup merely errored is telling them something untrue about their
    // own book. Any UNRECOGNISED state falls back to the not_in_book wording —
    // the likelier meaning, and never a raw enum shown to a person.
    const hint = isResolved
        ? GLOBAL_ASK_AICA_COPY.PICKER_RESOLVED_HINT
        : slot.state === ASK_BACK_SLOT_STATE.SEARCH_FAILED
        ? GLOBAL_ASK_AICA_COPY.PICKER_SEARCH_FAILED_HINT
        : GLOBAL_ASK_AICA_COPY.PICKER_NOT_IN_BOOK_HINT;

    // A resolved slot shows the MATCHED company name (that is the thing being made
    // visible); an unresolved one can only show what the user wrote.
    const label = isResolved ? slot.name || slot.written : slot.written;

    return (
        <div
            className={`${styles.StaticRow} ${isResolved ? styles.ResolvedRow : styles.ExcludedRow}`}
            data-testid={isResolved ? 'company-picker-resolved-row' : 'company-picker-excluded-row'}
        >
            <span className={styles.StaticMarker} aria-hidden="true">
                {isResolved ? '✓' : '!'}
            </span>
            <span className={styles.LogoSlot} aria-hidden="true" data-testid="company-picker-static-avatar">
                {(label || '?').charAt(0).toUpperCase()}
            </span>
            <span className={styles.CandidateName}>{label}</span>
            <span className={styles.StaticHint}>{hint}</span>
        </div>
    );
};

export default CompanyPicker;
